import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  created?: string;
  birthYear?: number;
  role: 'user' | 'admin';
  version?: number;
  instrument: string;
  skillLevel: string;
  genres: string[];
  availability: string[];
  location: {
    lat: number;
    lng: number;
  };
  address: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: Omit<User, 'id'> & { password: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

interface AuthTokenPayload {
  id?: string;
  username?: string;
  role?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const decodeJwtPayload = <T,>(token: string): T | null => {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const decoded = decodeURIComponent(
        Array.from(json)
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
      return JSON.parse(decoded) as T;
    } catch (error) {
      console.error('Failed to decode JWT payload:', error);
      return null;
    }
  };

  const getIdentityFromToken = (token: string): { id: string; email: string; role: 'user' | 'admin' } | null => {
    const payload = decodeJwtPayload<AuthTokenPayload>(token);
    if (!payload || typeof payload.id !== 'string' || typeof payload.username !== 'string') return null;

    const role = payload.role === 'admin' ? 'admin' : 'user';
    return { id: payload.id, email: payload.username, role };
  };

  const normalizeGenres = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .filter((genre) => typeof genre === 'string')
      .map((genre) => genre.trim())
      .filter((genre) => genre.length > 0);
  };

  const normalizeUser = (value: Partial<User> & { id: string; email: string; role?: 'user' | 'admin' }): User => {
    return {
      id: value.id,
      email: value.email,
      name: value.name ?? '',
      created: typeof value.created === 'string' ? value.created : undefined,
      birthYear: typeof value.birthYear === 'number' ? value.birthYear : undefined,
      role: value.role === 'admin' ? 'admin' : 'user',
      version: typeof value.version === 'number' ? value.version : undefined,
      instrument: value.instrument ?? '',
      skillLevel: value.skillLevel ?? '',
      genres: normalizeGenres(value.genres),
      availability: normalizeGenres(value.availability),
      location: value.location ?? { lat: 0, lng: 0 },
      address: value.address ?? '',
    };
  };

  // Check if user is already authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      if (apiService.isAuthenticated()) {
        const token = apiService.getToken();
        const identity = token ? getIdentityFromToken(token) : null;

        if (identity?.id) {
          try {
            const userData = await apiService.getUserById(identity.id);
            setUser(
              normalizeUser({
                ...userData,
                id: identity.id,
                email: userData?.email ?? identity.email,
                role: (userData?.role as User['role']) ?? identity.role,
              })
            );
            setIsLoading(false);
            return;
          } catch (error) {
            console.error('Failed to load user profile:', error);
          }
        }

        if (identity) {
          setUser(normalizeUser(identity));
        } else {
          apiService.removeToken();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await apiService.login(email, password);
      const token = apiService.getToken();
      const identity = token ? getIdentityFromToken(token) : null;
      if (!identity) {
        throw new Error('Unable to read authentication token.');
      }

      try {
        const userData = await apiService.getUserById(identity.id);
        setUser(
          normalizeUser({
            ...userData,
            id: identity.id,
            email: userData?.email ?? identity.email,
            role: (userData?.role as User['role']) ?? identity.role,
          })
        );
      } catch (error) {
        console.error('Failed to load user profile after login:', error);
        setUser(normalizeUser(identity));
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: Omit<User, 'id'> & { password: string }) => {
    setIsLoading(true);
    try {
      await apiService.register(userData);
      setUser(null);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user) return;
    
    try {
      const payloadVersion = typeof data.version === 'number' ? data.version : user.version ?? 0;
      const updatedUser = await apiService.updateProfile(user.id, { ...data, version: payloadVersion });
      setUser(
        normalizeUser({
          ...updatedUser,
          id: user.id,
          email: updatedUser?.email ?? user.email,
          role: updatedUser?.role ?? user.role,
        })
      );
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
